import { useEffect, useState } from 'react';
import { graphql } from '../gql/index.ts';
import { useQuery } from 'urql';
import { SeminarCard } from './SeminarCard.tsx';
import { Pagination } from './Pagination.tsx';
import { usePublicCompany } from './hooks/usePublicCompany.ts';
import { getFormattedSeminarDate } from './util.ts';
import { ErrorPage } from './ErrorPage.tsx';
import { PublicMenuSidebar } from './PublicMenuSidebar.tsx';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { ParticipantStatus } from '../gql/graphql.ts';
import { Loader } from '@progress/kendo-react-indicators';
import { useDeferredLoading } from '../shared/useDeferredLoading.ts';

const PublicSeminarsQuery = graphql(/* GraphQL */ `
  query getPublicSeminars(
    $companyId: String!
    $categoryId: String
    $limit: Int!
    $skip: Int!
    $searchTerm: String
  ) {
    publicSeminars(
      companyId: $companyId
      categoryId: $categoryId
      limit: $limit
      skip: $skip
      searchTerm: $searchTerm
    ) {
      seminars {
        id
        title
        subtitle
        canceled
        seminarNumber
        dateText
        description
        plainDescription
        locationText
        startDate
        endDate
        registrationDate
        isBookedOut
        isAlmostBookedOut
        signUps {
          canceled
        }
        currentUserIsLecturer
        lecturers {
          name
        }
        categories {
          name
        }
        participants {
          status
        }
      }
      count
    }
  }
`);

const pageSize = 5;

interface PublicSeminarListProps {
  companyId: string;
  categoryId?: string;
}

export const PublicSeminarList: React.FC<PublicSeminarListProps> = ({
  companyId,
  categoryId,
}) => {
  const navigate = useNavigate();
  const searchParamsFromUrl = useLocation({
    select: (l) => new URLSearchParams(l.searchStr ?? ''),
  });
  const searchStringFromUrl = searchParamsFromUrl.get('search') || null;
  const pageFromUrl = searchParamsFromUrl.get('listPage');

  const [currentPage, setCurrentPage] = useState(() => {
    const n = pageFromUrl ? parseInt(pageFromUrl, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n - 1 : 0;
  });

  // Mirror page into the URL so "Zurück" from a seminar restores it.
  // Use a number (not a string) — TanStack Router's default search serializer
  // JSON-encodes values, so a string would round-trip as ?listPage="5".
  useEffect(() => {
    const desired = currentPage === 0 ? null : currentPage + 1;
    const currentInUrl = pageFromUrl ? parseInt(pageFromUrl, 10) : null;
    if (
      desired ===
      (Number.isFinite(currentInUrl as number) ? currentInUrl : null)
    )
      return;
    navigate({
      search: ((prev: Record<string, unknown>) => {
        const { listPage: _listPage, ...rest } = prev;
        return desired === null ? rest : { ...rest, listPage: desired };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
      replace: true,
    });
  }, [currentPage, pageFromUrl, navigate]);

  const [prevCategoryId, setPrevCategoryId] = useState<string | undefined>(
    categoryId,
  );
  const [prevSearchString, setPrevSearchString] = useState<string | null>(
    searchStringFromUrl,
  );
  if (prevCategoryId !== categoryId) {
    setPrevCategoryId(categoryId);
    setCurrentPage(0);
  }
  if (prevSearchString !== searchStringFromUrl) {
    setPrevSearchString(searchStringFromUrl);
    setCurrentPage(0);
  }

  const [publicSeminarsQueryResult] = useQuery({
    query: PublicSeminarsQuery,
    variables: {
      companyId,
      categoryId,
      limit: pageSize,
      skip: currentPage * pageSize,
      searchTerm: searchStringFromUrl,
    },
    pause: companyId === undefined || companyId === null,
  });

  const { data, fetching } = publicSeminarsQueryResult;
  const showLoader = useDeferredLoading(fetching);
  const { publicSeminars } = data || {};
  const [publicCompany] = usePublicCompany();

  // Clamp page if the URL points past the last page of the current result set.
  const resultCount = publicSeminars?.count;
  if (resultCount != null) {
    const maxPage = resultCount > 0 ? Math.ceil(resultCount / pageSize) - 1 : 0;
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }

  const showSidebar =
    publicCompany?.functionalSettings?.searchActive ||
    Boolean(publicCompany?.sites?.length) ||
    Boolean(publicCompany?.categories?.length);

  return (
    <>
      {showSidebar && <PublicMenuSidebar publicCompany={publicCompany} />}
      <div
        className={`${showSidebar ? 'md:col-span-9 md:col-start-4' : 'md:col-span-12'} col-span-1 flex flex-col gap-6`}
      >
        {showLoader ? (
          <div className="flex justify-center py-16">
            <Loader type={'infinite-spinner'} size={'large'} />
          </div>
        ) : fetching ? null : publicSeminars?.count ? (
          <>
            <h2>Nächste Seminare</h2>
            {publicSeminars?.seminars.map((seminar) => {
              return (
                <SeminarCard
                  key={seminar?.id}
                  instructors={
                    seminar?.lecturers
                      ?.map((lecturer) => lecturer.name)
                      .filter(Boolean) as string[]
                  }
                  id={seminar?.seminarNumber}
                  title={seminar?.title}
                  time={getFormattedSeminarDate(
                    seminar.startDate,
                    seminar.endDate,
                    seminar.dateText,
                  )}
                  description={seminar?.plainDescription}
                  subtitle={seminar?.subtitle}
                  location={seminar?.locationText}
                  category={
                    seminar?.categories?.find((c) => Boolean(c.name.trim()))
                      ?.name
                  }
                  titleLink={'/seminar/' + seminar?.id}
                  isSignedUp={Boolean(
                    seminar.signUps.filter((s) => !s.canceled).length,
                  )}
                  isLecturer={Boolean(seminar.currentUserIsLecturer)}
                  isParticipant={seminar?.participants.some(
                    (p) => p.status === ParticipantStatus.Participant,
                  )}
                  isWaitlist={seminar?.participants.some(
                    (p) => p.status === ParticipantStatus.Waitlist,
                  )}
                  isParticipationCanceled={
                    seminar?.participants.some(
                      (p) => p.status === ParticipantStatus.Canceled,
                    ) || seminar?.signUps.some((s) => s.canceled)
                  }
                  isBookedOut={Boolean(seminar?.isBookedOut)}
                  isAlmostBookedOut={Boolean(seminar?.isAlmostBookedOut)}
                  showSignalLight={
                    publicCompany?.visualSettings.showSignalLight &&
                    !seminar.canceled
                  }
                  registrationDate={seminar?.registrationDate}
                />
              );
            })}
          </>
        ) : (
          <ErrorPage text="Keine Seminare gefunden" />
        )}
      </div>
      {Boolean(publicSeminars?.count) && (
        <div className="col-span-1 md:col-span-9 md:col-start-4">
          <Pagination
            onChange={(newPage) => {
              setCurrentPage(newPage);
              window.scrollTo({ top: 0, left: 0 });
            }}
            value={currentPage}
            countPages={Math.ceil((publicSeminars?.count ?? 0) / pageSize)}
          />
        </div>
      )}
    </>
  );
};
